// LOCATION PROVIDER — SCANNED & FIXED
// Clean, fully validated TypeScript + React code
// Precise-first (if required) with coarse fallback, compatibility fields, auto-retry on return from Settings

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";

import { PluginListenerHandle } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { App } from "@capacitor/app";
import { NativeSettings, AndroidSettings, IOSSettings } from "capacitor-native-settings";
import { isNativePlatform } from "../utils/platformUtil";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type LocationData = { latitude: number; longitude: number };

type PermissionStatus = "granted" | "denied" | "prompt" | "unknown";

type LocationContextType = {
  location: LocationData | null;
  error: string | null;
  declined: boolean;
  permissionStatus: PermissionStatus;

  // true when either precise OR coarse successfully returned a coordinate
  isLocationServiceEnabled: boolean | null;

  // new flag: true while fetching coordinates
  isFetchingLocation: boolean;

  showPermissionPopup: boolean;
  hidePermissionPopup: () => void;

  // compatibility fields (some UI components expect these)
  showDeviceLocationPopup: boolean;
  hideDeviceLocationPopup: () => void;

  setDeclined: (v: boolean) => void;
  requestLocation: () => Promise<void>;
  checkPermissionStatus: () => Promise<void>;
  openSettings: () => Promise<void>;
  allowLocationAccess: () => Promise<void>;
};

const LocationContext = createContext<LocationContextType | undefined>(undefined);

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const normalizePermission = (raw: any): PermissionStatus => {
  if (!raw) return "unknown";
  if (typeof raw === "string") {
    return raw === "granted" || raw === "denied" || raw === "prompt" ? raw : "unknown";
  }
  if (typeof raw === "object") {
    const p = raw.location || raw.fineLocation || raw.coarseLocation || raw.status;
    return p === "granted" || p === "denied" || p === "prompt" ? p : "unknown";
  }
  return "unknown";
};

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

export const LocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [location, setLocation] = useState<LocationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [declined, setDeclined] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>("unknown");

  const [showPermissionPopup, setShowPermissionPopup] = useState(false);
  const [showDeviceLocationPopup, setShowDeviceLocationPopup] = useState(false); // compatibility

  const [isLocationServiceEnabled, setIsLocationServiceEnabled] = useState<boolean | null>(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState<boolean>(false);

  const permissionRef = useRef<PermissionStatus>(permissionStatus);
  const mountedRef = useRef(true);

  useEffect(() => {
    permissionRef.current = permissionStatus;
  }, [permissionStatus]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const updatePermissionState = useCallback((p: PermissionStatus) => {
    if (!mountedRef.current) return;
    setPermissionStatus(p);
    permissionRef.current = p;
  }, []);

  // ---------------------------------------------------------------------------
  // Check permission
  // ---------------------------------------------------------------------------
  const checkPermissionStatus = useCallback(async () => {
    if (!isNativePlatform()) return;
    try {
      const raw = await Geolocation.checkPermissions();
      const perm = normalizePermission(raw);
      updatePermissionState(perm);
    } catch (e) {
      updatePermissionState("unknown");
    }
  }, [updatePermissionState]);

  const checkPermissionStatusRef = useRef(checkPermissionStatus);
  useEffect(() => {
    checkPermissionStatusRef.current = checkPermissionStatus;
  }, [checkPermissionStatus]);

  useEffect(() => {
    // initial check
    checkPermissionStatus();
  }, [checkPermissionStatus]);

  // ---------------------------------------------------------------------------
  // App returns to foreground → recheck permissions and auto-retry
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let handler: PluginListenerHandle | null = null;

    App.addListener("appStateChange", async (state) => {
      if (state.isActive) {
        await checkPermissionStatusRef.current?.();

        const perm = permissionRef.current;
        if (perm === "granted") {
          // hide custom permission popup and retry fetching location
          setShowPermissionPopup(false);
          // try to fetch location automatically but do not block
          try {
            await requestLocation();
          } catch (e) {
            // swallow - requestLocation manages its own errors
          }
        }
      }
    }).then((h) => {
      handler = h;
    });

    return () => {
      if (handler) handler.remove(); // do not return Promise
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Open OS settings
  // ---------------------------------------------------------------------------
  const openSettings = useCallback(async () => {
    if (!isNativePlatform()) return;
    try {
      await NativeSettings.open({ optionAndroid: AndroidSettings.ApplicationDetails, optionIOS: IOSSettings.App });
    } catch (e) {
      // ignore
    }
  }, []);

  // ---------------------------------------------------------------------------
  // requestLocation: precise -> coarse fallback. sets flags and fetching state.
  // ---------------------------------------------------------------------------
  const requestLocation = useCallback(async (): Promise<void> => {
    setIsFetchingLocation(true);
    setError(null);

    try {
      // 1. Check and request permissions if needed
      const raw = await Geolocation.checkPermissions();
      let perm = normalizePermission(raw);

      if (perm !== "granted") {
        const req = await Geolocation.requestPermissions();
        perm = normalizePermission(req);
        updatePermissionState(perm);
        if (perm === "denied") {
          setDeclined(true);
          setShowPermissionPopup(true);
          setIsLocationServiceEnabled(false);
          setError("Location permission denied.");
          return;
        }
      }

      // 2. Try precise (GPS/high-accuracy) first
      try {
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 5000 });
        setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setIsLocationServiceEnabled(true);
        setError(null);
        return;
      } catch (e) {
        // if precise fails (GPS off or denied), we will fallback to coarse
      }

      // 3. Coarse fallback
      try {
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 8000 });
        setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setIsLocationServiceEnabled(true);
        setError(null);
        return;
      } catch (e) {
        // coarse failed too
      }

      // 4. Nothing worked
      setIsLocationServiceEnabled(false);
      setError("Unable to get location.");
    } catch (err: any) {
      setIsLocationServiceEnabled(false);
      setError(err?.message ?? "Failed to get location.");
    } finally {
      setIsFetchingLocation(false);
    }
  }, [updatePermissionState]);

  // ---------------------------------------------------------------------------
  // allowLocationAccess: UI entrypoint
  // ---------------------------------------------------------------------------
  const allowLocationAccess = useCallback(async (): Promise<void> => {
    await checkPermissionStatusRef.current?.();
    const perm = permissionRef.current;
    if (perm !== "granted") {
      setShowPermissionPopup(true);
      return;
    }

    await requestLocation();
  }, [requestLocation]);

  // ---------------------------------------------------------------------------
  // Provider value
  // ---------------------------------------------------------------------------
  return (
    <LocationContext.Provider
      value={{
        location,
        error,
        declined,
        permissionStatus,
        isLocationServiceEnabled,
        isFetchingLocation,

        showPermissionPopup,
        hidePermissionPopup: () => setShowPermissionPopup(false),

        // compatibility
        showDeviceLocationPopup,
        hideDeviceLocationPopup: () => setShowDeviceLocationPopup(false),

        setDeclined,
        requestLocation,
        checkPermissionStatus,
        openSettings,
        allowLocationAccess,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------
export const useLocationAccess = (): LocationContextType => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error("useLocationAccess must be used inside LocationProvider");
  return ctx;
};
